import { useDispatch, useSelector } from 'react-redux'
import { getCurrentRoom } from './model/interaction-scheme'
import { TrainingRoom } from 'room/training-room/training-room'
import { WebinarRoom } from 'room/training-room/webinar-room'
import { translate } from 'intl/translate'
import { RootState } from 'common/redux'
import { setEventsSourcePath } from 'event-source-manager/redux'
import { EventsSourcePath, RoomAddress, RoomType, User } from 'common/types'
import { FC, useCallback, useEffect } from 'react'
import { setJoinedRoom } from './redux'
import { GroupRoom } from 'room/group-room/group-room'
import { ConfiguredVideoConferenceMedia } from 'video-conference-media/configured-video-conference-media'
import { assertUnreachable } from 'utils/unreachable'
import { useJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useWorkflowUser } from 'room/common/use-workflow-user'
import { Lobby } from 'room/lobby'
import {
	ActivityHeartBeat,
	PresenceHeartBeat,
} from 'workflow/presentation/heart-beat'
import SystemNotifications from 'interaction-scheme/system-notifications'
import PublicChatRoom from 'room/public-chat'
import useInfoEventSource from 'session-info/use-session-info-source'
import { useAuthenticatedBeeldayClient } from 'connectivity/beelday-hooks'
import { JunoProvider } from '../vcr/juno/juno-provider'
import DevTools from 'room/common/presentation/devtools'
import { ProvideVideoComponents } from 'video-conference-media'
import { BitrateManager } from 'vcr/juno/bitrate-manager'
import useAutomaticRecordingTransition from 'features/recording/use-automatic-recording-transition'
import RecordingStateController from 'features/recording/recording-state-controller'
import { useSessionMode } from 'session-mode/use-session-mode'

type DispatchedRoomProps = {
	address: RoomAddress
	user: User
}

const DispatchedRoom: FC<DispatchedRoomProps> = ({ address, user }) => {
	const beeldayClient = useAuthenticatedBeeldayClient()
	/**
	 * In-person cuts video at the mount point rather than deeper down: no
	 * `ConfiguredVideoConferenceMedia`, so nothing ever asks for a camera or a
	 * microphone, and no `BitrateManager`, which has no stream to manage.
	 *
	 * `<JunoProvider>` below deliberately stays mounted in both modes. What it
	 * connects with is `videoConferenceMedia.vcrId` (`selectVCRId`, passed to
	 * `useJunoInstance`, where the parameter is confusingly named
	 * `vcrAddress`), and `vcrId` is only ever set by
	 * `SSE_CONFERENCE_CALL_EXPECTED` (`video-conference-media/redux.ts`), which
	 * a stationary scheme never sends. Without it `useJunoInstance` returns
	 * `undefined`, every consumer already handles `juno === undefined`, and so
	 * removing the provider would change the context tree for no gain. Note
	 * this is *not* `vcrRoomAddress`, a separate field set on every room entry.
	 */
	const hasVideo = useSessionMode() !== 'IN_PERSON'

	useInfoEventSource()

	const onBeat = useCallback(
		(secondsSinceLastActivity: number) =>
			beeldayClient.heartBeat(
				address.workflowId,
				user.id,
				secondsSinceLastActivity
			),
		[beeldayClient, address.workflowId, user.id]
	)

	switch (address.roomType) {
		case RoomType.GroupRoom:
			return (
				<>
					<ActivityHeartBeat onBeat={onBeat} />
					<GroupRoom />
					<ConfiguredVideoConferenceMedia />
				</>
			)
		case RoomType.TrainingRoom:
			// Only this room is gated: a stationary interaction scheme builds a
			// training room and a lobby and nothing else, so the group room,
			// webinar room and public chats below are unreachable in-person and
			// stay exactly as they are.
			return (
				<>
					<ActivityHeartBeat onBeat={onBeat} />
					<TrainingRoom />
					{hasVideo ? (
						<>
							<BitrateManager />
							<ConfiguredVideoConferenceMedia />
						</>
					) : null}
				</>
			)
		case RoomType.WebinarRoom:
			return (
				<>
					<ActivityHeartBeat onBeat={onBeat} />
					<WebinarRoom />
					<ConfiguredVideoConferenceMedia />
				</>
			)
		case RoomType.Lobby:
			return (
				<>
					<PresenceHeartBeat onBeat={onBeat} />
					<Lobby />
				</>
			)

		case RoomType.PublicChat:
			return (
				<>
					<PresenceHeartBeat onBeat={onBeat} />
					<PublicChatRoom />
					<ConfiguredVideoConferenceMedia />
				</>
			)
		default:
			assertUnreachable('RoomType', address.roomType)
	}
}

export const TrainingInteractionScheme: FC = () => {
	const dispatch = useDispatch()
	const joinedRoomAddress = useJoinedRoomAddress()
	const latestInteractionSchemeEvent = useSelector(
		(state: RootState) => state.eventSource.latestInteractionSchemeEvent
	)
	const workflowUser = useWorkflowUser()
	useAutomaticRecordingTransition()

	useEffect(() => {
		if (latestInteractionSchemeEvent && workflowUser) {
			const newJoinedRoom = getCurrentRoom(
				latestInteractionSchemeEvent,
				workflowUser.id
			)
			if (newJoinedRoom && newJoinedRoom.roomId !== joinedRoomAddress?.roomId) {
				dispatch(setJoinedRoom(newJoinedRoom))
				dispatch(setEventsSourcePath(EventsSourcePath.room))
			}
		}
	}, [dispatch, joinedRoomAddress, latestInteractionSchemeEvent, workflowUser])

	if (joinedRoomAddress && workflowUser) {
		return (
			<JunoProvider>
				<RecordingStateController>
					<ProvideVideoComponents>
						<DispatchedRoom address={joinedRoomAddress} user={workflowUser} />
					</ProvideVideoComponents>
					<SystemNotifications />
				</RecordingStateController>
				<DevTools />
			</JunoProvider>
		)
	}
	return <div>{translate('loading')}</div>
}
